import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { savePDF } from './savePDF';

// ── Page constants ────────────────────────────────────────────────────────────
const PW   = 210;   // A4 width mm
const PH   = 297;   // A4 height mm
const ML   = 14;    // left margin
const MR   = 14;    // right margin
const CW   = PW - ML - MR; // 182mm content width

// ── Colour palette ────────────────────────────────────────────────────────────
const C = {
  maroon:    [177, 18,  23],
  orange:    [212, 120, 28],
  green:     [46,  139, 87],
  amber:     [230, 126, 34],
  red:       [192, 57,  43],
  blue:      [41,  128, 185],
  purple:    [142, 68,  173],
  teal:      [22,  160, 133],
  dark:      [44,  62,  80],
  gray:      [108, 117, 125],
  lightGray: [225, 229, 233],
  white:     [255, 255, 255],
  bgCard:    [249, 250, 251],
  bgPage:    [252, 252, 253],
};

// ── Utility helpers ───────────────────────────────────────────────────────────

const getBase64Image = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

const fmt = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const fmtLong = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';

const pct = (n, d) => (d > 0 ? `${Math.round((n / d) * 100)}%` : '0%');

const lighten = (rgb, factor = 0.88) =>
  rgb.map(v => Math.round(v + (255 - v) * factor));

// ── Date ranges ───────────────────────────────────────────────────────────────

const getRange = (reportType, selectedMonth) => {
  const now = new Date();
  if (reportType === 'daily') {
    const s = new Date(now); s.setHours(0, 0, 0, 0);
    const e = new Date(now); e.setHours(23, 59, 59, 999);
    return { start: s, end: e, label: fmtLong(s), shortLabel: fmt(s) };
  }
  if (reportType === 'weekly') {
    const dow = now.getDay();
    const s = new Date(now);
    s.setDate(now.getDate() - (dow === 0 ? 6 : dow - 1));
    s.setHours(0, 0, 0, 0);
    const e = new Date(s);
    e.setDate(s.getDate() + 6);
    e.setHours(23, 59, 59, 999);
    return { start: s, end: e, label: `${fmt(s)} – ${fmt(e)}`, shortLabel: 'This Week' };
  }
  const [yr, mo] = selectedMonth.split('-').map(Number);
  const s = new Date(yr, mo - 1, 1, 0, 0, 0, 0);
  const e = new Date(yr, mo, 0, 23, 59, 59, 999);
  const monthName = s.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  return { start: s, end: e, label: monthName, shortLabel: monthName };
};

const filterEntries = (all, start, end) =>
  all.filter(e => {
    if (e.status === 'draft') return false;
    const d = new Date(e.metadata?.submittedAt || e.createdAt);
    return d >= start && d <= end;
  });

const buildWeeklyCompliance = (allEntries, allStudents, rangeStart, rangeEnd) => {
  const weeks = [];
  let weekStart = new Date(rangeStart);
  const dow = weekStart.getDay();
  if (dow !== 1) weekStart.setDate(weekStart.getDate() - (dow === 0 ? 6 : dow - 1));

  while (weekStart <= rangeEnd) {
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);
    const effStart = new Date(Math.max(weekStart.getTime(), rangeStart.getTime()));
    const effEnd   = new Date(Math.min(weekEnd.getTime(),   rangeEnd.getTime()));
    effEnd.setHours(23, 59, 59, 999);

    const submittedIds = new Set();
    allEntries.forEach(e => {
      if (e.status === 'draft') return;
      const d = new Date(e.metadata?.submittedAt || e.createdAt);
      if (d >= effStart && d <= effEnd) {
        const sid = (e.studentId?._id || e.studentId)?.toString();
        if (sid) submittedIds.add(sid);
      }
    });

    const compliance = allStudents.length > 0
      ? Math.round((submittedIds.size / allStudents.length) * 100) : 0;

    weeks.push({
      label: `${fmt(effStart)} – ${fmt(effEnd)}`,
      submitted: submittedIds.size,
      total: allStudents.length,
      compliance,
    });

    weekStart = new Date(weekEnd);
    weekStart.setDate(weekStart.getDate() + 1);
  }
  return weeks;
};

// ── jsPDF drawing helpers ─────────────────────────────────────────────────────

const checkPage = (doc, y, needed = 20) => {
  if (y + needed > PH - 18) { doc.addPage(); return 18; }
  return y;
};

const drawSectionTitle = (doc, title, y, color = C.maroon) => {
  y = checkPage(doc, y, 18);
  // Accent block
  doc.setFillColor(...color);
  doc.rect(ML, y, 3.5, 8, 'F');
  // Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(...color);
  doc.text(title.toUpperCase(), ML + 6, y + 6);
  // Separator
  doc.setDrawColor(...C.lightGray);
  doc.setLineWidth(0.3);
  doc.line(ML, y + 10, PW - MR, y + 10);
  return y + 14;
};

const drawStatCards = (doc, cards, y) => {
  const n = Math.min(cards.length, 4);
  const gap = 4;
  const cardW = (CW - gap * (n - 1)) / n;
  const cardH = 24;
  y = checkPage(doc, y, cardH + 6);

  cards.forEach((card, i) => {
    const x = ML + i * (cardW + gap);
    // Background
    doc.setFillColor(...C.bgCard);
    doc.roundedRect(x, y, cardW, cardH, 2, 2, 'F');
    // Top accent strip
    doc.setFillColor(...card.color);
    doc.roundedRect(x, y, cardW, 2.5, 1, 1, 'F');
    // Value
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(...card.color);
    doc.text(String(card.value), x + cardW / 2, y + 14, { align: 'center' });
    // Label
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...C.gray);
    const lines = doc.splitTextToSize(card.label, cardW - 4);
    doc.text(lines[0] || '', x + cardW / 2, y + 20, { align: 'center' });
  });

  return y + cardH + 6;
};

const drawInsightBox = (doc, lines, y, color = C.blue) => {
  const lineH = 5;
  const padV  = 4;
  const boxH  = lines.length * lineH + padV * 2;
  y = checkPage(doc, y, boxH + 6);

  // Light tinted background
  doc.setFillColor(...lighten(color, 0.90));
  doc.roundedRect(ML, y, CW, boxH, 2, 2, 'F');
  // Left accent bar
  doc.setFillColor(...color);
  doc.rect(ML, y, 3, boxH, 'F');
  // Text
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...C.dark);
  lines.forEach((line, i) => {
    doc.text(line, ML + 6, y + padV + 3.5 + i * lineH);
  });
  return y + boxH + 6;
};

const drawBarChart = (doc, items, y, color, maxLabelChars = 32) => {
  if (!items || items.length === 0) return y;
  const barH  = 6.5;
  const gap   = 3.5;
  const labelW = 58;
  const valW   = 16;
  const barAreaW = CW - labelW - valW - 2;
  const maxVal = Math.max(...items.map(i => i.value), 1);
  const totalH = items.length * (barH + gap);
  y = checkPage(doc, y, totalH + 4);

  items.forEach((item, i) => {
    const ry = y + i * (barH + gap);
    const label = item.label.length > maxLabelChars
      ? item.label.slice(0, maxLabelChars - 1) + '…' : item.label;

    // Label
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...C.dark);
    doc.text(label, ML, ry + barH - 1);

    // BG track
    doc.setFillColor(...C.lightGray);
    doc.roundedRect(ML + labelW, ry, barAreaW, barH, 1.5, 1.5, 'F');

    // Value bar
    if (item.value > 0) {
      const bw = Math.max((item.value / maxVal) * barAreaW, 2);
      doc.setFillColor(...color);
      doc.roundedRect(ML + labelW, ry, bw, barH, 1.5, 1.5, 'F');
    }

    // Value label
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...(item.value > 0 ? color : C.gray));
    doc.text(
      String(item.value) + (item.suffix || ''),
      ML + labelW + barAreaW + 2.5,
      ry + barH - 1
    );
  });

  return y + totalH + 5;
};

// ── Page header ───────────────────────────────────────────────────────────────

const drawPageHeader = async (doc, logoBase64, collegeName, courseName, courseCode, reportType, range, batchLabel) => {
  const hH = 40;
  // Background
  doc.setFillColor(...C.maroon);
  doc.rect(0, 0, PW, hH, 'F');

  // Logo
  let textX = ML;
  if (logoBase64) {
    try {
      doc.addImage(logoBase64, 'PNG', ML, 6, 22, 22);
      textX = ML + 26;
    } catch (_) {}
  }

  // College name
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12.5);
  doc.setTextColor(...C.white);
  const nameLines = doc.splitTextToSize(collegeName, PW - textX - MR - 44);
  doc.text(nameLines, textX, 14);

  // Subtitle
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(220, 190, 190);
  doc.text('Department E-Logbook Report', textX, 14 + nameLines.length * 5.5);

  // Report type badge (top-right)
  const badge = reportType.charAt(0).toUpperCase() + reportType.slice(1) + ' Report';
  const bW = 40; const bH = 9;
  doc.setFillColor(...C.orange);
  doc.roundedRect(PW - MR - bW, 7, bW, bH, 2, 2, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...C.white);
  doc.text(badge, PW - MR - bW / 2, 12.8, { align: 'center' });

  // Meta line
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(230, 210, 210);
  const meta = `Course: ${courseName} (${courseCode})   ·   Period: ${range.label}` +
    (batchLabel ? `   ·   Batch: ${batchLabel}` : '') +
    `   ·   Generated: ${fmt(new Date())}`;
  doc.text(meta, ML + (logoBase64 ? 26 : 0), hH - 5);

  // Orange separator
  doc.setDrawColor(...C.orange);
  doc.setLineWidth(1.2);
  doc.line(0, hH, PW, hH);

  return hH + 8;
};

// ── Page footer ───────────────────────────────────────────────────────────────

const drawPageFooter = (doc, pageNum, totalPages, collegeName) => {
  const fy = PH - 9;
  doc.setDrawColor(...C.lightGray);
  doc.setLineWidth(0.3);
  doc.line(ML, fy - 3, PW - MR, fy - 3);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  doc.setTextColor(...C.gray);
  doc.text(collegeName, ML, fy);
  doc.text(`Page ${pageNum} of ${totalPages}`, PW - MR, fy, { align: 'right' });
  doc.text('Confidential — E-Logbook Department Report', PW / 2, fy, { align: 'center' });
};

// ── autoTable shared config ───────────────────────────────────────────────────

const tableDefaults = (headColor) => ({
  margin: { left: ML, right: MR },
  headStyles: {
    fillColor: headColor,
    textColor: C.white,
    fontSize: 8,
    fontStyle: 'bold',
    cellPadding: 3,
  },
  bodyStyles: {
    fontSize: 7.5,
    cellPadding: 2.5,
    textColor: C.dark,
    lineColor: C.lightGray,
    lineWidth: 0.15,
  },
  tableLineColor: C.lightGray,
  tableLineWidth: 0.2,
});

// ── Student Activity Report ───────────────────────────────────────────────────

export const generateStudentActivityReport = async ({ data, collegeName, logoSrc, filters }) => {
  const { periodLabel, activeStudents, inactiveStudents } = data;
  const logoBase64 = logoSrc ? await getBase64Image(logoSrc) : null;

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });

  // ── Header ──
  const hH = 40;
  doc.setFillColor(...C.maroon);
  doc.rect(0, 0, PW, hH, 'F');

  let textX = ML;
  if (logoBase64) {
    try { doc.addImage(logoBase64, 'PNG', ML, 6, 22, 22); textX = ML + 26; } catch (_) {}
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12.5);
  doc.setTextColor(...C.white);
  const nameLines = doc.splitTextToSize(collegeName, PW - textX - MR - 44);
  doc.text(nameLines, textX, 14);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(220, 190, 190);
  doc.text('Student Activity Report', textX, 14 + nameLines.length * 5.5);

  const bW = 46; const bH = 9;
  doc.setFillColor(...C.orange);
  doc.roundedRect(PW - MR - bW, 7, bW, bH, 2, 2, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...C.white);
  doc.text('Student Report', PW - MR - bW / 2, 12.8, { align: 'center' });

  const filterParts = [
    `Period: ${periodLabel}`,
    filters.course ? `Course: ${filters.course}` : null,
    filters.batch ? `Batch: ${filters.batch}` : null,
    `Generated: ${fmt(new Date())}`,
  ].filter(Boolean).join('   ·   ');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(230, 210, 210);
  doc.text(filterParts, textX, hH - 5);

  doc.setDrawColor(...C.orange);
  doc.setLineWidth(1.2);
  doc.line(0, hH, PW, hH);

  let y = hH + 8;

  // ── Summary cards ──
  y = drawStatCards(doc, [
    { label: 'Active Students',   value: activeStudents.length,   color: C.green },
    { label: 'Inactive Students', value: inactiveStudents.length, color: C.red },
    { label: 'Total Students',    value: activeStudents.length + inactiveStudents.length, color: C.blue },
    { label: 'Total Entries',     value: activeStudents.reduce((s, r) => s + r.entryCount, 0), color: C.orange },
  ], y);

  // ── Active students table ──
  y = drawSectionTitle(doc, `Active Students — ${activeStudents.length} submitted entries`, y, C.green);
  if (activeStudents.length === 0) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(...C.gray);
    doc.text('No active students for this period.', ML, y + 6);
    y += 14;
  } else {
    autoTable(doc, {
      ...tableDefaults(C.green),
      startY: y,
      head: [['#', 'Name', 'Reg. No.', 'Course', 'Batch', 'Entries']],
      body: activeStudents.map((s, i) => [i + 1, s.name, s.registrationNumber, s.course, s.batch, s.entryCount]),
      columnStyles: {
        0: { halign: 'center', cellWidth: 10 },
        2: { halign: 'center', cellWidth: 28 },
        4: { halign: 'center', cellWidth: 22 },
        5: { halign: 'center', cellWidth: 18, fontStyle: 'bold', textColor: C.green },
      },
      alternateRowStyles: { fillColor: lighten(C.green, 0.96) },
    });
    y = doc.lastAutoTable.finalY + 10;
  }

  // ── Inactive students table ──
  y = checkPage(doc, y, 20);
  y = drawSectionTitle(doc, `Inactive Students — ${inactiveStudents.length} with no entries in period`, y, C.red);
  if (inactiveStudents.length === 0) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(...C.gray);
    doc.text('All students were active in this period.', ML, y + 6);
    y += 14;
  } else {
    autoTable(doc, {
      ...tableDefaults(C.red),
      startY: y,
      head: [['#', 'Name', 'Reg. No.', 'Course', 'Batch', 'Account Status']],
      body: inactiveStudents.map((s, i) => [i + 1, s.name, s.registrationNumber, s.course, s.batch, s.isAccountActive ? 'Active' : 'Deactivated']),
      columnStyles: {
        0: { halign: 'center', cellWidth: 10 },
        2: { halign: 'center', cellWidth: 28 },
        4: { halign: 'center', cellWidth: 22 },
        5: { halign: 'center', cellWidth: 26 },
      },
      alternateRowStyles: { fillColor: lighten(C.red, 0.96) },
    });
  }

  // ── Footers ──
  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    drawPageFooter(doc, p, totalPages, collegeName);
  }

  const safeLabel = periodLabel.replace(/[^a-zA-Z0-9]/g, '_');
  savePDF(doc, `Student_Activity_Report_${safeLabel}.pdf`);
};

// ── Main export ───────────────────────────────────────────────────────────────

export const generateMonitorReport = async ({
  reportType,
  selectedMonth,
  allEntries,
  allStudents,
  courseTeachers,
  modules,
  assignedCourse,
  collegeName,
  logoSrc,
  batch,
}) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const logoBase64 = logoSrc ? await getBase64Image(logoSrc) : null;
  const range      = getRange(reportType, selectedMonth);
  const { start, end } = range;

  // ── Restrict to a single batch, if requested ─────────────────────────────────
  if (batch) {
    allStudents = allStudents.filter(s =>
      s.assignedCourses?.some(c => c.courseId?.toString() === assignedCourse?._id?.toString() && c.batch === batch)
    );
    const batchStudentIds = new Set(allStudents.map(s => (s._id || s.id)?.toString()));
    allEntries = allEntries.filter(e => batchStudentIds.has((e.studentId?._id || e.studentId)?.toString()));
  }

  // ── Filtered entries for this period ────────────────────────────────────────
  const periodEntries = filterEntries(allEntries, start, end);

  // ── Teacher stats ────────────────────────────────────────────────────────────
  const teacherMap = {};
  courseTeachers.forEach(t => {
    const id = (t.teacherId?._id || t.teacherId)?.toString();
    if (id) teacherMap[id] = { id, name: t.name || 'Unknown', periodVerif: 0, totalVerif: 0, lastVerif: null };
  });
  // All-time totals
  allEntries.forEach(e => {
    if (!e.review?.reviewedAt) return;
    const rid = (e.review.reviewerId?._id || e.review.reviewerId)?.toString();
    if (!rid) return;
    if (!teacherMap[rid]) {
      teacherMap[rid] = { id: rid, name: e.review.reviewerName || 'Unknown', periodVerif: 0, totalVerif: 0, lastVerif: null };
    }
    teacherMap[rid].totalVerif++;
    const rd = new Date(e.review.reviewedAt);
    if (!teacherMap[rid].lastVerif || rd > teacherMap[rid].lastVerif) teacherMap[rid].lastVerif = rd;
  });
  // Period verifications
  periodEntries.forEach(e => {
    if (!e.review?.reviewedAt) return;
    const rid = (e.review.reviewerId?._id || e.review.reviewerId)?.toString();
    if (rid && teacherMap[rid]) teacherMap[rid].periodVerif++;
  });
  const teacherStats = Object.values(teacherMap).sort((a, b) => b.periodVerif - a.periodVerif);
  const totalPeriodVerif = teacherStats.reduce((s, t) => s + t.periodVerif, 0);

  // ── Student stats ────────────────────────────────────────────────────────────
  const studentStats = allStudents.map(s => {
    const sid  = (s._id || s.id)?.toString();
    const name = `${s.profile?.firstName || ''} ${s.profile?.lastName || ''}`.trim();
    const entries  = periodEntries.filter(e => (e.studentId?._id || e.studentId)?.toString() === sid);
    const verified = entries.filter(e => e.status === 'approved').length;
    const pending  = entries.filter(e => ['submitted', 'under_review'].includes(e.status)).length;
    let lastSub = null;
    entries.forEach(e => {
      const d = new Date(e.metadata?.submittedAt || e.createdAt);
      if (!lastSub || d > lastSub) lastSub = d;
    });
    const verifierCounts = {};
    entries.forEach(e => {
      if (e.review?.reviewerName) verifierCounts[e.review.reviewerName] = (verifierCounts[e.review.reviewerName] || 0) + 1;
    });
    const primaryVerifier = Object.entries(verifierCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || '—';
    return { name, total: entries.length, verified, pending, lastSub, primaryVerifier };
  }).sort((a, b) => b.total - a.total);

  // ── Module stats ─────────────────────────────────────────────────────────────
  const moduleMap = {};
  periodEntries.forEach(e => {
    const mid   = e.moduleId?.toString();
    const mInfo = modules.find(m => (m.moduleId || m._id)?.toString() === mid);
    const mName = mInfo?.name || 'Other';
    if (!moduleMap[mName]) moduleMap[mName] = { count: 0, students: new Set() };
    moduleMap[mName].count++;
    const sid = (e.studentId?._id || e.studentId)?.toString();
    if (sid) moduleMap[mName].students.add(sid);
  });
  const moduleStats = Object.entries(moduleMap)
    .map(([name, d]) => ({ name, count: d.count, students: d.students.size }))
    .sort((a, b) => b.count - a.count);

  // ── Overview ─────────────────────────────────────────────────────────────────
  const totalStudents  = allStudents.length;
  const totalFaculty   = courseTeachers.length;
  const totalEntries   = periodEntries.length;
  const totalApproved  = periodEntries.filter(e => e.status === 'approved').length;
  const totalPending   = periodEntries.filter(e => ['submitted', 'under_review'].includes(e.status)).length;
  const verifRate      = totalEntries > 0 ? Math.round((totalApproved / totalEntries) * 100) : 0;
  const activeStudents = new Set(periodEntries.map(e => (e.studentId?._id || e.studentId)?.toString())).size;

  // ── Weekly compliance (monthly only) ─────────────────────────────────────────
  const weeklyData = reportType === 'monthly'
    ? buildWeeklyCompliance(allEntries, allStudents, start, end) : [];

  // ── Insights ─────────────────────────────────────────────────────────────────
  const topGuide    = teacherStats[0];
  const lazyGuides  = teacherStats.filter(t => t.periodVerif === 0);
  const zeroStudents = studentStats.filter(s => s.total === 0);
  const topModule   = moduleStats[0];
  const avgEntries  = totalStudents > 0 ? (totalEntries / totalStudents).toFixed(1) : '0';

  const insightLines = [
    `• ${totalEntries} logbook ${totalEntries === 1 ? 'entry' : 'entries'} submitted by ${activeStudents} of ${totalStudents} students` +
      ` (${pct(activeStudents, totalStudents)} participation rate, avg ${avgEntries} entries/student).`,
    topGuide?.periodVerif > 0
      ? `• Most active guide: ${topGuide.name} — ${topGuide.periodVerif} verification${topGuide.periodVerif !== 1 ? 's' : ''} this period.`
      : '• No guide has performed any verifications during this period.',
    lazyGuides.length > 0
      ? `• Guides with no verifications this period: ${lazyGuides.map(g => g.name).join(', ')}.`
      : '• All guides have verified at least one logbook this period.',
    zeroStudents.length > 0
      ? `• ${zeroStudents.length} student${zeroStudents.length !== 1 ? 's have' : ' has'} not submitted any entries — immediate follow-up recommended.`
      : '• All students have submitted at least one logbook entry this period.',
    topModule
      ? `• Most-used module: "${topModule.name}" with ${topModule.count} entries from ${topModule.students} unique students.`
      : '',
    `• Overall verification rate: ${verifRate}% (${totalApproved} approved, ${totalPending} pending).`,
  ].filter(Boolean);

  // ─────────────────────────────────────────────────────────────────────────────
  // ASSEMBLE PDF
  // ─────────────────────────────────────────────────────────────────────────────

  let y = await drawPageHeader(
    doc, logoBase64,
    collegeName,
    assignedCourse?.name || '',
    assignedCourse?.code || '',
    reportType,
    range,
    batch || null
  );

  // ── 1. Executive Summary ──────────────────────────────────────────────────
  y = drawSectionTitle(doc, 'Executive Summary', y, C.maroon);
  y = drawStatCards(doc, [
    { value: totalStudents,  label: 'Total Students',         color: C.blue   },
    { value: totalFaculty,   label: 'Faculty / Guides',       color: C.purple },
    { value: totalEntries,   label: 'Entries This Period',    color: C.orange },
    { value: `${verifRate}%`,label: 'Verification Rate',
      color: verifRate >= 70 ? C.green : verifRate >= 40 ? C.amber : C.red },
  ], y);
  y = drawStatCards(doc, [
    { value: activeStudents,                  label: 'Active Students',       color: C.teal   },
    { value: totalStudents - activeStudents,  label: 'Inactive Students',
      color: totalStudents - activeStudents > 0 ? C.red : C.green },
    { value: totalPeriodVerif,               label: 'Verifications Done',     color: C.purple },
    { value: totalApproved,                  label: 'Entries Approved',       color: C.green  },
  ], y);

  // ── 2. Key Insights ───────────────────────────────────────────────────────
  y = drawSectionTitle(doc, 'Key Insights & Analysis', y, C.blue);
  y = drawInsightBox(doc, insightLines, y, C.blue);

  // ── 3. Guide / Faculty Activity ───────────────────────────────────────────
  y = drawSectionTitle(doc, 'Guide / Faculty Verification Activity', y, C.purple);

  if (teacherStats.length > 0) {
    y = drawBarChart(doc, teacherStats.map(t => ({ label: t.name, value: t.periodVerif })), y, C.purple);

    y = checkPage(doc, y, 20);
    autoTable(doc, {
      startY: y,
      ...tableDefaults(C.purple),
      alternateRowStyles: { fillColor: lighten(C.purple, 0.95) },
      head: [['Guide Name', 'This Period', '% of Verif.', 'All-Time', 'Last Activity', 'Status']],
      body: teacherStats.map(t => [
        t.name,
        t.periodVerif,
        totalPeriodVerif > 0 ? pct(t.periodVerif, totalPeriodVerif) : '—',
        t.totalVerif,
        t.lastVerif ? fmt(t.lastVerif) : 'Never',
        t.periodVerif === 0 ? 'Inactive' : t.periodVerif >= 5 ? 'Active' : 'Low',
      ]),
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 22, halign: 'center' },
        2: { cellWidth: 24, halign: 'center' },
        3: { cellWidth: 22, halign: 'center' },
        4: { cellWidth: 30, halign: 'center' },
        5: { cellWidth: 34, halign: 'center' },
      },
      didParseCell: (data) => {
        if (data.section !== 'body') return;
        if (data.column.index === 5) {
          const v = String(data.cell.raw);
          if (v === 'Inactive') { data.cell.styles.textColor = C.red;    data.cell.styles.fontStyle = 'bold'; }
          else if (v === 'Active') { data.cell.styles.textColor = C.green; data.cell.styles.fontStyle = 'bold'; }
          else { data.cell.styles.textColor = C.amber; }
        }
        if (data.column.index === 1 && parseInt(data.cell.raw) === 0) {
          data.cell.styles.textColor = C.red;
        }
      },
    });
    y = doc.lastAutoTable.finalY + 8;
  } else {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8.5);
    doc.setTextColor(...C.gray);
    doc.text('No guide data available.', ML, y);
    y += 10;
  }

  // ── 4. Student Logbook Activity ───────────────────────────────────────────
  y = checkPage(doc, y, 20);
  y = drawSectionTitle(doc, 'Student Logbook Activity', y, C.teal);

  autoTable(doc, {
    startY: y,
    ...tableDefaults(C.teal),
    alternateRowStyles: { fillColor: lighten(C.teal, 0.94) },
    head: [['Student Name', 'Submitted', 'Verified', 'Pending', 'Last Submission', 'Primary Verifier']],
    body: studentStats.map(s => [
      s.name,
      s.total,
      s.verified,
      s.pending,
      s.lastSub ? fmt(s.lastSub) : '—',
      s.primaryVerifier,
    ]),
    columnStyles: {
      0: { cellWidth: 46 },
      1: { cellWidth: 20, halign: 'center' },
      2: { cellWidth: 20, halign: 'center' },
      3: { cellWidth: 20, halign: 'center' },
      4: { cellWidth: 30, halign: 'center' },
      5: { cellWidth: 46 },
    },
    didParseCell: (data) => {
      if (data.section !== 'body') return;
      if (data.column.index === 1 && parseInt(data.cell.raw) === 0) {
        data.cell.styles.textColor = C.red;
        data.cell.styles.fontStyle = 'bold';
      }
      if (data.column.index === 3 && parseInt(data.cell.raw) > 0) {
        data.cell.styles.textColor = C.amber;
      }
    },
  });
  y = doc.lastAutoTable.finalY + 8;

  // ── 5. Module-wise Activity ───────────────────────────────────────────────
  if (moduleStats.length > 0) {
    y = checkPage(doc, y, 20);
    y = drawSectionTitle(doc, 'Module-wise Activity Breakdown', y, C.orange);

    y = drawBarChart(doc, moduleStats.map(m => ({ label: m.name, value: m.count })), y, C.orange);

    y = checkPage(doc, y, 20);
    autoTable(doc, {
      startY: y,
      ...tableDefaults(C.orange),
      alternateRowStyles: { fillColor: lighten(C.orange, 0.94) },
      head: [['Module Name', 'Total Entries', 'Unique Students', '% of Entries']],
      body: moduleStats.map(m => [
        m.name,
        m.count,
        m.students,
        pct(m.count, totalEntries),
      ]),
      columnStyles: {
        0: { cellWidth: 82 },
        1: { cellWidth: 30, halign: 'center' },
        2: { cellWidth: 34, halign: 'center' },
        3: { cellWidth: 36, halign: 'center' },
      },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // ── 6. Weekly Compliance (monthly only) ───────────────────────────────────
  if (reportType === 'monthly' && weeklyData.length > 0) {
    y = checkPage(doc, y, 20);
    y = drawSectionTitle(doc, 'Weekly Compliance Breakdown', y, C.green);

    y = drawBarChart(
      doc,
      weeklyData.map((w, i) => ({ label: `Wk ${i + 1}: ${w.label}`, value: w.compliance, suffix: '%' })),
      y, C.green
    );

    y = checkPage(doc, y, 20);
    autoTable(doc, {
      startY: y,
      ...tableDefaults(C.green),
      alternateRowStyles: { fillColor: lighten(C.green, 0.94) },
      head: [['Week', 'Students Submitted', 'Total Students', 'Compliance', 'Rating']],
      body: weeklyData.map((w, i) => [
        `Week ${i + 1}: ${w.label}`,
        w.submitted,
        w.total,
        `${w.compliance}%`,
        w.compliance >= 80 ? 'Good' : w.compliance >= 50 ? 'Average' : 'Low',
      ]),
      columnStyles: {
        0: { cellWidth: 68 },
        1: { cellWidth: 28, halign: 'center' },
        2: { cellWidth: 28, halign: 'center' },
        3: { cellWidth: 28, halign: 'center' },
        4: { cellWidth: 30, halign: 'center' },
      },
      didParseCell: (data) => {
        if (data.section !== 'body' || data.column.index !== 4) return;
        const v = String(data.cell.raw);
        if (v === 'Low')     { data.cell.styles.textColor = C.red;   data.cell.styles.fontStyle = 'bold'; }
        else if (v === 'Good') { data.cell.styles.textColor = C.green; data.cell.styles.fontStyle = 'bold'; }
        else                   { data.cell.styles.textColor = C.amber; }
      },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // ── Footers on all pages ──────────────────────────────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    drawPageFooter(doc, p, totalPages, collegeName);
  }

  // ── Save ──────────────────────────────────────────────────────────────────
  const dateStr    = new Date().toISOString().slice(0, 10);
  const typeLabel  = reportType.charAt(0).toUpperCase() + reportType.slice(1);
  const courseCode = assignedCourse?.code || 'Dept';
  const batchPart  = batch ? `_${batch.replace(/[^a-zA-Z0-9]/g, '')}` : '';
  await savePDF(doc, `${courseCode}_${typeLabel}_Report${batchPart}_${dateStr}.pdf`);
};
